// FLAGS: none
// The list view of "Spaces Schedules": the language, type and "only mine" filters (?language=, ?type=, ?mine=1,
// shared with the calendar) over the week-paged list. With type "All", each session carries its type as a pill.
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ResponseConveneHostableSessionResponse } from "@src/api/v1/data-contracts";
import HostFilterControls from "@src/views/HostSignups/HostFilterControls";
import HostSignupsWeekList from "@src/views/HostSignups/HostSignupsWeekList";
import {
  HOSTABLE_SESSION_TYPE,
  HostCalendarState,
  hostableMatchesFilters,
  readHostCalendarState,
  sessionTypeLabelKey,
} from "@src/views/HostSignups/calendar/hostCalendar";

type FilterState = Pick<HostCalendarState, "language" | "type" | "mine">;

interface HostSignupsListProps {
  sessions: ResponseConveneHostableSessionResponse[];
  busy: boolean;
  onSignUp: (sessionId: string) => void;
  onWithdraw: (sessionId: string) => void;
}

const HostSignupsList = ({
  sessions,
  busy,
  onSignUp,
  onWithdraw,
}: HostSignupsListProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, type, mine } = readHostCalendarState(
    searchParams,
    new Date(),
  );
  const filters: FilterState = { language, type, mine };
  const languages = Array.from(
    new Set(sessions.flatMap((s) => (s.languageCode ? [s.languageCode] : []))),
  ).sort((a, b) => a.localeCompare(b));
  const visible = sessions.filter((s) => hostableMatchesFilters(s, filters));

  // A filter change starts over on the first week that still has sessions.
  const update = (partial: Partial<FilterState>) => {
    const next = { ...filters, ...partial };
    const params = new URLSearchParams(searchParams);
    const put = (key: string, value: string | null) =>
      value ? params.set(key, value) : params.delete(key);
    put("language", next.language);
    put("type", next.type);
    put("mine", next.mine ? "1" : null);
    params.delete("week");
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      <div className="mb-4">
        <HostFilterControls
          state={filters}
          languages={languages}
          onChange={update}
        />
      </div>
      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-700">
          {t("HostSignups.List.NoFilterMatches")}
        </div>
      ) : (
        <HostSignupsWeekList
          sessions={visible}
          busy={busy}
          typeLabel={
            type ? undefined : t(sessionTypeLabelKey[HOSTABLE_SESSION_TYPE])
          }
          onSignUp={onSignUp}
          onWithdraw={onWithdraw}
        />
      )}
    </div>
  );
};

export default HostSignupsList;
